import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionHeader,
  AccordionIconButton,
  AccordionItem,
  AccordionPanel,
  Box,
  FormControl,
  ListRow,
  renderListRows,
  Skeleton,
  useThemedValueSwitch,
} from '@tq/ui-components';

import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { Consultation, ApplicationResponse } from 'types';
import { useApplicationDetails } from './hooks';
import { DataItemConfiguration } from 'types/DataItemConfiguration';

export type DetailsProps = {
  consultation?: Consultation;
  application?: ApplicationResponse;
  dataItemsToDisplay?: DataItemConfiguration[];
};

export const ApplicationDetails: React.FC<DetailsProps> = ({
  consultation,
  application,
  dataItemsToDisplay,
}) => {
  const { t } = useTranslation('case_details_tab');
  const { tvs } = useThemedValueSwitch();
  const isMobile = useIsMobile();
  const columnLayout = isMobile ? '45% 55%' : '325px auto';
  const { applicationDetails, importantDates, contactDetails } = useApplicationDetails(
    application,
    consultation,
    dataItemsToDisplay
  );

  if (!consultation) {
    // Do proper skeletons
    return <Skeleton />;
  }

  const getRenderListConfig = () => {
    return {
      p: '4 6',
      addLabelColons: false,
      zebraRows: true,
      accessibleTextForEmptyValue: t('no_value', 'No value'),
    };
  };

  const rowMargin = -1;

  const accordionPanelPadding = tvs(
    {
      tq: {
        p: 0,
      },
    },
    {
      py: 0,
    }
  );

  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <AccordionItem mb={4}>
        <h2>
          <AccordionHeader>
            <Box flex='1' textAlign='left'>
              {t('application_details', 'Application details')}
            </Box>
            <AccordionIconButton data-testid='application_details_accordion_toggle' />
          </AccordionHeader>
        </h2>
        <AccordionPanel sx={accordionPanelPadding}>
          <FormControl>
            <ListRow listItemColumns={columnLayout} mx={[rowMargin, 0]}>
              {renderListRows(applicationDetails, getRenderListConfig())}
            </ListRow>
          </FormControl>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem mb={4}>
        <h2>
          <AccordionHeader>
            <Box flex='1' textAlign='left'>
              {t('contact_details', 'Contact details')}
            </Box>
            <AccordionIconButton data-testid='contact_details_accordion_toggle' />
          </AccordionHeader>
        </h2>
        <AccordionPanel sx={accordionPanelPadding}>
          <FormControl>
            <ListRow listItemColumns={columnLayout} mx={[rowMargin, 0]}>
              {renderListRows(contactDetails, getRenderListConfig())}
            </ListRow>
          </FormControl>
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem mb={4}>
        <h2>
          <AccordionHeader>
            <Box flex='1' textAlign='left'>
              {t('important_dates', 'Important dates')}
            </Box>
            <AccordionIconButton data-testid='important_dates_accordion_toggle' />
          </AccordionHeader>
        </h2>
        <AccordionPanel sx={accordionPanelPadding}>
          <FormControl>
            <ListRow listItemColumns={columnLayout} mx={[rowMargin, 0]}>
              {renderListRows(importantDates, getRenderListConfig())}
            </ListRow>
          </FormControl>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
